<script setup lang="ts">
import { EPopover, formatBinding, isApplePlatform, useShortcut } from '@erpx/ui'
import { computed, nextTick, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import AppTile from '@/components/AppTile.vue'
import FsdBadge from '@/components/FsdBadge.vue'
import {
  foldForSearch,
  scoreNavItem,
  textMatches,
  useCurrentApp,
} from '@/composables/useNavPrefs'
import { useUserScope } from '@/composables/useUserScope'
import {
  HOME_APP_ID,
  appItems,
  appLandingPath,
  isExtraneous,
  type NavApp,
} from '@/nav/apps'
import { navLabel, navPartLabel, type NavItem } from '@/nav/items'
import { useAuthStore } from '@/stores/auth'

/**
 * The app launcher: the top bar's 9-dot button and the panel it opens.
 *
 * Two jobs, one panel. With an empty filter it is a grid of every app the
 * user may open — the same tiles, in the same order, as the home screen, so
 * the launcher is "the home screen, from anywhere". Typing turns it into a
 * search over app names AND the screens inside them, because the question
 * people actually bring to a launcher is rarely "which app" — it is "where is
 * Bank statements", and the answer should be one keystroke away, not two
 * clicks and a scan of a sidebar.
 *
 * Tiles and results are real links (middle-click opens a tab, the status bar
 * shows the destination). Results are also listbox options driven from the
 * filter input with ↑/↓/Enter — the WAI-ARIA combobox pattern, the same one
 * the ⌘K palette uses, so focus never leaves the field while choosing.
 *
 * Permission filtering is `visibleApps`, the same filter the sidebar and the
 * palette use (CLAUDE.md §6): an app with no reachable screen has no tile.
 */

/**
 * ⌘. / Ctrl+. — checked against the bindings already taken (⌘K, ⌘/, ⌘⇧N, ?,
 * ⌘⇧I, ⌘S, ⌘↵) and against browser defaults: none of Chrome, Firefox or Edge
 * reserves Ctrl+. on Windows/Linux, and a letter-free binding cannot collide
 * with a document screen's own accelerators. Registered here, beside the open
 * state it toggles, rather than in AppShell: this component is mounted for
 * exactly as long as the shell is, so the cheat sheet lists it on every
 * screen all the same.
 */
const LAUNCHER_SHORTCUT = 'mod+.'
const MAX_SCREENS = 8

const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const userScope = useUserScope()
const { apps, current } = useCurrentApp(userScope, auth.can)

const open = ref(false)
const query = ref('')
const activeIndex = ref(0)
const inputEl = ref<HTMLInputElement | null>(null)
const gridEl = ref<HTMLElement | null>(null)
const resultsEl = ref<HTMLElement | null>(null)

const inputId = useId()
const listId = useId()

const shortcutLabel = computed(() => formatBinding(LAUNCHER_SHORTCUT))
const ariaShortcut = computed(() => (isApplePlatform() ? 'Meta+.' : 'Control+.'))

const launcherApps = computed(() => apps.value.filter((entry) => entry.app.inLauncher))
const homeApp = computed(() => apps.value.find((entry) => entry.app.id === HOME_APP_ID)?.app)

const folded = computed(() => foldForSearch(query.value))
const searching = computed(() => folded.value !== '')

const matchedApps = computed(() =>
  searching.value
    ? launcherApps.value.filter(({ app, part }) =>
        textMatches(folded.value, t(app.labelKey), t(app.descriptionKey), navPartLabel(part)),
      )
    : [],
)

interface ScreenHit {
  app: NavApp
  item: NavItem
  score: number
  order: number
}

/**
 * Screens across every app the user can open, Home included — Home has no
 * tile, but Approvals is exactly the kind of screen people search for. No
 * screen is listed in two apps today; if one ever is, it appears once, under
 * the first app in launcher order.
 */
const matchedScreens = computed<ScreenHit[]>(() => {
  if (!searching.value) return []
  const seen = new Set<string>()
  const hits: ScreenHit[] = []
  let order = 0
  for (const { app, part } of apps.value) {
    for (const item of appItems(part)) {
      if (seen.has(item.id)) continue
      seen.add(item.id)
      const score = scoreNavItem(item, folded.value)
      if (score > 0) hits.push({ app, item, score, order: order++ })
    }
  }
  return hits.sort((a, b) => b.score - a.score || a.order - b.order).slice(0, MAX_SCREENS)
})

interface LauncherOption {
  key: string
  to: string
}

/** Every result in the order the listbox renders it — apps first, then screens. */
const options = computed<LauncherOption[]>(() => [
  ...matchedApps.value.map(({ app, part }) => ({ key: `app-${app.id}`, to: appLandingPath(part) })),
  ...matchedScreens.value.map(({ item }) => ({ key: `screen-${item.id}`, to: item.to })),
])

const optionId = (index: number): string => `${listId}-option-${index}`
const activeId = computed(() =>
  searching.value && options.value.length > 0 ? optionId(activeIndex.value) : undefined,
)

watch(query, () => {
  activeIndex.value = 0
})

watch(open, (isOpen) => {
  if (!isOpen) return
  query.value = ''
  activeIndex.value = 0
  // The popover's own focus scope already lands on the first non-link
  // tabbable (this input); doing it explicitly keeps that true if the panel
  // ever gains a button above the field.
  void nextTick(() => inputEl.value?.focus())
})

// Back/Forward while the panel is open: the panel was about the page the user
// just left.
watch(
  () => route.path,
  () => {
    open.value = false
  },
)

useShortcut(
  LAUNCHER_SHORTCUT,
  () => {
    open.value = !open.value
  },
  {
    allowInInput: true,
    describe: { id: 'global-apps', description: 'shell.shortcuts.apps', group: 'global' },
  },
)

function close(): void {
  open.value = false
}

function go(to: string): void {
  close()
  void router.push(to)
}

function scrollActiveIntoView(): void {
  resultsEl.value?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
}

function tiles(): HTMLElement[] {
  return [...(gridEl.value?.querySelectorAll<HTMLElement>('[data-launcher-tile]') ?? [])]
}

function onInputKeydown(event: KeyboardEvent): void {
  if (!searching.value) {
    // An empty filter has no listbox; ↓ steps into the tile grid instead.
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      tiles()[0]?.focus()
    }
    return
  }

  const count = options.value.length
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (count > 0) activeIndex.value = (activeIndex.value + 1) % count
      void nextTick(scrollActiveIntoView)
      break
    case 'ArrowUp':
      event.preventDefault()
      if (count > 0) activeIndex.value = (activeIndex.value - 1 + count) % count
      void nextTick(scrollActiveIntoView)
      break
    case 'Enter': {
      event.preventDefault()
      const option = options.value[activeIndex.value]
      if (option) go(option.to)
      break
    }
    default:
      break
  }
}

/**
 * Arrow keys move between tiles the way they move between icons on a phone:
 * along the row, and a whole row up or down. The column count is read from
 * the laid-out grid rather than assumed, because it changes at the narrow
 * breakpoint; the horizontal keys follow the reading direction under RTL.
 */
function onGridKeydown(event: KeyboardEvent): void {
  const grid = gridEl.value
  if (grid === null) return
  const list = tiles()
  const index = list.findIndex((tile) => tile === document.activeElement)
  if (index < 0) return

  const style = getComputedStyle(grid)
  const columns = Math.max(1, style.gridTemplateColumns.split(' ').filter((track) => track !== '').length)
  const step = style.direction === 'rtl' ? -1 : 1

  let next: number
  switch (event.key) {
    case 'ArrowRight':
      next = index + step
      break
    case 'ArrowLeft':
      next = index - step
      break
    case 'ArrowDown':
      next = index + columns
      break
    case 'ArrowUp':
      if (index < columns) {
        event.preventDefault()
        inputEl.value?.focus()
        return
      }
      next = index - columns
      break
    case 'Home':
      next = 0
      break
    case 'End':
      next = list.length - 1
      break
    default:
      return
  }

  event.preventDefault()
  list[Math.min(Math.max(next, 0), list.length - 1)]?.focus()
}
</script>

<template>
  <EPopover v-model:open="open" side="bottom" align="start" :label="t('shell.launcher.title')">
    <template #trigger>
      <button
        type="button"
        class="topbar__launcher"
        :class="{ 'is-open': open }"
        :aria-label="t('shell.launcher.trigger')"
        aria-haspopup="dialog"
        :aria-expanded="open"
        :aria-keyshortcuts="ariaShortcut"
        :title="t('shell.launcher.triggerHint', { shortcut: shortcutLabel })"
      >
        <AppIcon name="apps" :size="17" :stroke-width="2.5" />
      </button>
    </template>

    <div class="launcher">
      <div class="launcher__head">
        <h2 class="launcher__title">{{ t('shell.launcher.title') }}</h2>
        <RouterLink v-if="homeApp" to="/" class="launcher__home" @click="close">
          <AppTile :app="homeApp" :size="20" />
          <span>{{ t(homeApp.labelKey) }}</span>
        </RouterLink>
      </div>

      <div class="launcher__search">
        <AppIcon name="search" :size="14" class="launcher__search-icon" />
        <input
          :id="inputId"
          ref="inputEl"
          v-model="query"
          class="launcher__input"
          type="text"
          role="combobox"
          autocomplete="off"
          spellcheck="false"
          :placeholder="t('shell.launcher.searchPlaceholder')"
          :aria-label="t('shell.launcher.searchLabel')"
          :aria-expanded="searching && options.length > 0"
          aria-autocomplete="list"
          :aria-controls="listId"
          :aria-activedescendant="activeId"
          @keydown="onInputKeydown"
        />
      </div>

      <!-- Filtering: one listbox, apps then screens. -->
      <div
        v-show="searching && options.length > 0"
        :id="listId"
        ref="resultsEl"
        class="launcher__results"
        role="listbox"
        :aria-label="t('shell.launcher.resultsLabel')"
      >
        <template v-if="matchedApps.length > 0">
          <p class="launcher__group" role="presentation">{{ t('shell.launcher.appsGroup') }}</p>
          <RouterLink
            v-for="({ app, part }, index) in matchedApps"
            :id="optionId(index)"
            :key="`app-${app.id}`"
            :to="appLandingPath(part)"
            class="launcher__option"
            role="option"
            tabindex="-1"
            :aria-selected="activeIndex === index"
            :data-active="activeIndex === index"
            @click="close"
            @mousemove="activeIndex = index"
          >
            <AppTile :app="app" :size="28" />
            <span class="launcher__option-body">
              <span class="launcher__option-label">{{ t(app.labelKey) }}</span>
              <span class="launcher__option-meta">{{ t(app.descriptionKey) }}</span>
            </span>
          </RouterLink>
        </template>

        <template v-if="matchedScreens.length > 0">
          <p class="launcher__group" role="presentation">{{ t('shell.launcher.screensGroup') }}</p>
          <RouterLink
            v-for="(hit, index) in matchedScreens"
            :id="optionId(matchedApps.length + index)"
            :key="`screen-${hit.item.id}`"
            :to="hit.item.to"
            class="launcher__option"
            role="option"
            tabindex="-1"
            :aria-selected="activeIndex === matchedApps.length + index"
            :data-active="activeIndex === matchedApps.length + index"
            @click="close"
            @mousemove="activeIndex = matchedApps.length + index"
          >
            <AppTile :app="hit.app" :size="22" />
            <span class="launcher__option-body">
              <span class="launcher__option-label">{{ navLabel(hit.item) }}</span>
              <span class="launcher__option-meta">{{ t(hit.app.labelKey) }}</span>
            </span>
            <FsdBadge v-if="isExtraneous(hit.item)" />
          </RouterLink>
        </template>
      </div>

      <div v-if="searching && options.length === 0" class="launcher__empty" role="status">
        <p class="launcher__empty-title">{{ t('shell.launcher.emptyTitle', { query: query.trim() }) }}</p>
        <p class="launcher__empty-text">{{ t('shell.launcher.emptyHint') }}</p>
      </div>

      <!-- No filter: the grid. -->
      <nav
        v-show="!searching"
        ref="gridEl"
        class="launcher__grid"
        :aria-label="t('shell.launcher.gridLabel')"
        @keydown="onGridKeydown"
      >
        <RouterLink
          v-for="{ app, part } in launcherApps"
          :key="app.id"
          :to="appLandingPath(part)"
          class="launcher__tile"
          :class="{ 'is-current': current?.app.id === app.id }"
          :aria-current="current?.app.id === app.id ? 'true' : undefined"
          data-launcher-tile
          @click="close"
        >
          <AppTile :app="app" :size="48" />
          <span class="launcher__tile-label">{{ t(app.labelKey) }}</span>
        </RouterLink>
      </nav>
    </div>
  </EPopover>
</template>

<style>
/*
 * The launcher reuses EPopover for positioning, dismissal and focus return,
 * but not for its box: the shared panel caps itself at a select's 20rem and
 * pads for a form. `:has()` targets only the panel that holds a launcher, so
 * every other popover keeps its defaults.
 */
.e-popover__panel:has(> .launcher) {
  max-height: none;
  padding: 0;
  overflow: hidden;
  border-radius: var(--erpx-radius-lg);
}

.launcher {
  display: flex;
  flex-direction: column;
  inline-size: min(640px, calc(100vw - 32px));
  max-block-size: min(560px, calc(100vh - 96px));
}

.launcher__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--erpx-space-3);
  padding: var(--erpx-space-3) var(--erpx-space-4) var(--erpx-space-2);
}

.launcher__title {
  margin: 0;
  color: var(--erpx-color-text-primary);
  font-size: var(--erpx-text-md);
  font-weight: var(--erpx-weight-semibold);
  letter-spacing: var(--erpx-tracking-tight);
}

.launcher__home {
  display: inline-flex;
  align-items: center;
  gap: var(--erpx-space-2);
  padding: var(--erpx-space-1) var(--erpx-space-2);
  border-radius: var(--erpx-radius);
  color: var(--erpx-color-text-secondary);
  font-size: var(--erpx-text-xs);
  font-weight: var(--erpx-weight-medium);
  text-decoration: none;
}

.launcher__home:hover {
  background-color: var(--erpx-color-surface-hover);
  color: var(--erpx-color-text-primary);
}

.launcher__search {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-2);
  margin: 0 var(--erpx-space-4) var(--erpx-space-2);
  padding-inline: var(--erpx-space-3);
  border: 1px solid var(--erpx-color-border-default);
  border-radius: var(--erpx-radius);
  background-color: var(--erpx-color-surface-sunken);
  color: var(--erpx-color-text-muted);
}

.launcher__search:focus-within {
  border-color: var(--erpx-color-accent-border);
  box-shadow: var(--erpx-focus-ring);
}

.launcher__input {
  flex: 1;
  min-width: 0;
  height: 34px;
  border: none;
  background: none;
  color: var(--erpx-color-text-primary);
  font: inherit;
  font-size: var(--erpx-text-sm);
  outline: none;
  /* `.launcher__search:focus-within` draws the one ring; not the global one too. */
  box-shadow: none;
}

.launcher__input::placeholder {
  color: var(--erpx-color-text-disabled);
}

.launcher__grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--erpx-space-1);
  padding: var(--erpx-space-2) var(--erpx-space-3) var(--erpx-space-4);
  overflow-y: auto;
}

@media (max-width: 560px) {
  .launcher__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.launcher__tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--erpx-space-2);
  padding: var(--erpx-space-3) var(--erpx-space-1) var(--erpx-space-2);
  border-radius: var(--erpx-radius-lg);
  color: var(--erpx-color-text-secondary);
  text-decoration: none;
  transition: background-color var(--erpx-duration-fast) var(--erpx-easing-standard);
}

.launcher__tile .app-tile {
  transition: transform var(--erpx-duration-fast) var(--erpx-easing-standard);
}

.launcher__tile:hover {
  background-color: var(--erpx-color-surface-hover);
  color: var(--erpx-color-text-primary);
}

.launcher__tile:hover .app-tile {
  transform: translateY(-1px);
}

.launcher__tile.is-current {
  background-color: var(--erpx-color-surface-selected);
  color: var(--erpx-color-text-primary);
}

.launcher__tile-label {
  max-inline-size: 100%;
  overflow: hidden;
  font-size: var(--erpx-text-xs);
  font-weight: var(--erpx-weight-medium);
  line-height: var(--erpx-leading-tight);
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.launcher__tile:focus-visible,
.launcher__home:focus-visible {
  outline: none;
  box-shadow: var(--erpx-focus-ring);
}

.launcher__results {
  flex: 1;
  min-height: 0;
  padding: 0 var(--erpx-space-2) var(--erpx-space-3);
  overflow-y: auto;
}

.launcher__group {
  margin: 0;
  padding: var(--erpx-space-2) var(--erpx-space-2) var(--erpx-space-1);
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
  font-weight: var(--erpx-weight-semibold);
  letter-spacing: var(--erpx-tracking-wide);
  text-transform: uppercase;
}

.launcher__option {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-3);
  min-height: 40px;
  padding: var(--erpx-space-1) var(--erpx-space-2);
  border-radius: var(--erpx-radius);
  color: var(--erpx-color-text-primary);
  text-decoration: none;
}

.launcher__option[data-active='true'] {
  background-color: var(--erpx-color-surface-selected);
}

.launcher__option-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  line-height: var(--erpx-leading-tight);
}

.launcher__option-label {
  overflow: hidden;
  font-size: var(--erpx-text-sm);
  font-weight: var(--erpx-weight-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.launcher__option-meta {
  overflow: hidden;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.launcher__empty {
  padding: var(--erpx-space-6) var(--erpx-space-4);
  text-align: center;
}

.launcher__empty-title {
  margin: 0 0 var(--erpx-space-1);
  color: var(--erpx-color-text-primary);
  font-weight: var(--erpx-weight-medium);
}

.launcher__empty-text {
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-xs);
}

@media (forced-colors: active) {
  .launcher__option[data-active='true'],
  .launcher__tile.is-current {
    outline: 1px solid Highlight;
  }
}
</style>
